import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Scale, ArrowRight, CheckCircle, Sparkles, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Venteliste = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/waitlist/signup`, {
        email,
        name
      });
      
      setIsSubmitted(true);
      toast.success('✅ Du er nå på ventelisten!');
    } catch (error) {
      toast.error('Noe gikk galt. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center px-6">
        <div className="text-center max-w-2xl">
          <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-emerald-600/40">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Du er på listen! 🎉
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            Vi sender deg en e-post når vi lanserer.
            <br />
            <strong className="text-slate-900">Eksklusiv rabatt</strong> venter på deg.
          </p>
          <button
            onClick={() => navigate('/')}
            className="text-slate-600 hover:text-slate-900 underline"
          >
            Tilbake til forsiden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      
      {/* Header - Clean */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5">
          <div className="flex justify-between items-center">
            <button type="button" onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <div className="w-7 h-7 bg-slate-900 rounded-lg flex items-center justify-center">
                <Scale className="w-4 h-4 text-white" />
              </div>
              <span className="text-base font-bold text-slate-900">Advokat for alle</span>
            </button>
            <div className="px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
              <span className="text-sm font-bold text-emerald-700">Lanseres November 2025</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero - Adams Matkasse style (founder-forward) */}
      <section className="min-h-screen pt-20 pb-16 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-16 lg:gap-20 items-center">
            
            {/* LEFT: Advokat - Generic */}
            <div className="order-2 lg:order-1">
              <div className="bg-white rounded-2xl border border-slate-300 shadow-2xl overflow-hidden">
                <div className="h-[500px] overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_repo-assistant-5/artifacts/12fnh6ai_nano-banana-2025-11-16T07-39-35.png"
                    alt="Advokat"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="bg-slate-900 px-8 py-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-white text-lg">Våre Advokater</h3>
                      <p className="text-slate-300 text-sm">Autoriserte MNA-advokater</p>
                    </div>
                    <div className="px-3 py-1.5 bg-emerald-500 rounded-md">
                      <span className="text-white text-xs font-bold">MNA</span>
                    </div>
                  </div>
                  <p className="text-slate-200 text-sm leading-relaxed border-l-3 border-emerald-500 pl-4">
                    Profesjonell juridisk rådgivning fra autoriserte advokater. Moderne teknologi gjør det tilgjengelig til en ærlig pris.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Right: Balanced - engaging but clean */}
            <div className="order-1 lg:order-2 space-y-7 lg:space-y-9">
              
              {/* Headline - Better responsive breaks */}
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
                  <span className="text-slate-900">Advokat til </span>
                  <span className="text-slate-400 line-through whitespace-nowrap">4.990 kr</span>
                  {' '}
                  <span className="text-slate-900">eller</span>
                  <br />
                  <span className="text-emerald-600 text-5xl sm:text-6xl md:text-7xl lg:text-8xl">499 kr?</span>
                </h1>
              </div>
              
              {/* Subline */}
              <p className="text-xl sm:text-2xl text-slate-700 font-light leading-relaxed">
                Samme autoriserte advokat.
                <br />
                <span className="text-slate-900 font-semibold">Bare bedre teknologi.</span>
              </p>
              
              {/* Key benefits - Professional icons */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
                    <Scale className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-700 font-medium">Autoriserte MNA-advokater</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-700 font-medium">Svar innen 15 minutter</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-700 font-medium"><strong className="text-emerald-700">50% rabatt</strong> til de første 500</span>
                </div>
              </div>
              
              {/* Email form */}
              <form onSubmit={handleSubmit} className="bg-white border-2 border-slate-300 rounded-2xl p-2 shadow-xl">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="email"
                    placeholder="Din e-post"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 h-14 border-0 focus-visible:ring-0 text-base px-4"
                    required
                  />
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="bg-slate-900 hover:bg-slate-800 text-white h-14 px-8 rounded-xl font-bold text-base"
                  >
                    {isLoading ? 'Sender...' : 'Meld meg på'}
                  </Button>
                </div>
              </form>
              
              <p className="text-sm text-slate-500">
                Ingen kredittkort. Ingen binding. Kun info om lansering.
              </p>
              
            </div>
            
          </div>
        </div>
      </section>

    </div>
  );
};

export default Venteliste;
